import React from 'react';
import { useParams } from 'react-router-dom';
import { formatISO, isValid, parseISO, startOfDay } from 'date-fns';
import styles from './UpcomingOrdersPrintPage.module.scss';
import { OrdersService } from '../../services/ordersService';
import { IOrderDetails, OrderFulfillmentStatus } from '@ready/dashboardv2api.contracts';
import { useSearchParams } from '../../hooks/useSearchParams';
import filterOrders from '../../utils/orderUtils/filterOrders';
import LocationsService from '../../locations/services/LocationsService';

interface PageParams {
  contextId: string;
  locationId: string;
  day: string;
}

type HydratedOrder = IOrderDetails;

const concurrencyLimit = 5;

const UpcomingOrdersPrintPage = () => {
  const { contextId, locationId, day } = useParams<PageParams>();
  const { query, from, to } = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [orders, setOrders] = React.useState<HydratedOrder[]>([]);
  const [locationTitle, setLocationTitle] = React.useState<string>('');

  const normalizeDay = React.useMemo(() => {
    const today = startOfDay(new Date());
    const parsedDay = parseISO(day);
    const dayValue = isValid(parsedDay) ? parsedDay : today;
    return formatISO(dayValue, { representation: 'date' });
  }, [day]);

  const fromDate = React.useMemo(() => {
    return from ? parseISO(from) : null;
  }, [from]);

  const toDate = React.useMemo(() => {
    return to ? parseISO(to) : null;
  }, [to]);

  React.useEffect(() => {
    let cancelled = false;

    const hydrateOrders = async (baseOrders: IOrderDetails[]): Promise<IOrderDetails[]> => {
      const results: IOrderDetails[] = new Array(baseOrders.length);

      let index = 0;
      const runNext = async () => {
        while (index < baseOrders.length) {
          const currentIndex = index++;
          const base = baseOrders[currentIndex];
          try {
            const detail = await OrdersService.getOrder(contextId, locationId, base.orderId);
            results[currentIndex] = detail;
          } catch {
            // fallback to base if detail fetch fails
            results[currentIndex] = base;
          }
        }
      };

      const runners = Array.from({ length: Math.min(concurrencyLimit, baseOrders.length) }, () => runNext());
      await Promise.all(runners);
      return results;
    };

    const load = async () => {
      try {
        setLoading(true);
        let list: IOrderDetails[];

        if (fromDate && toDate && isValid(fromDate) && isValid(toDate)) {
          // Fetch orders from date range
          const startDate = startOfDay(fromDate);
          const endDate = startOfDay(toDate);
          list = await OrdersService.getOrdersByRange(contextId, locationId, startDate, endDate);
        } else {
          // Default single day behavior
          list = await OrdersService.getOrderList(contextId, locationId, normalizeDay);
        }

        // Filter out completed and cancelled orders to match UpcomingOrdersListPage behavior
        const upcomingOrders = list.filter(order =>
          order.status !== OrderFulfillmentStatus.Complete &&
          order.status !== OrderFulfillmentStatus.Cancelled
        );
        const searchTerm = decodeURIComponent(query || '');
        const filtered = filterOrders(upcomingOrders, searchTerm);
        const hydrated = await hydrateOrders(filtered);
        if (!cancelled) {
          setOrders(hydrated);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadLocation = async () => {
      try {
        const loc = await LocationsService.getClientLocationById(contextId, locationId);
        const name = (loc as any)?.displayName || (loc as any)?.name || locationId;
        if (!cancelled) setLocationTitle(name);
      } catch {
        if (!cancelled) setLocationTitle(locationId);
      }
    };

    load();
    loadLocation();
    return () => {
      cancelled = true;
    };
  }, [contextId, locationId, normalizeDay, query, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!loading && orders.length) {
      const id = setTimeout(() => window.print(), 200);
      return () => clearTimeout(id);
    }
  }, [loading, orders.length]);

  const groups = React.useMemo(() => {
    const overdue: HydratedOrder[] = [];
    const upNext: HydratedOrder[] = [];
    const offset: HydratedOrder[] = [];
    const dineIn: HydratedOrder[] = [];

    for (const o of orders) {
      if (o.tableInfo?.tableName) {
        dineIn.push(o);
        continue;
      }
      if (o.status === OrderFulfillmentStatus.Pending) {
        // simple grouping similar to list page semantics
        upNext.push(o);
      } else {
        // treat other non-complete as overdue; in practice the list page has richer logic
        overdue.push(o);
      }
    }

    // naive offset grouping: any order with pickup more than 30 minutes from now
    const now = new Date();
    for (let i = upNext.length - 1; i >= 0; i--) {
      const o = upNext[i];
      const minutesUntil = (new Date(o.pickupTime).getTime() - now.getTime()) / 60000;
      if (minutesUntil >= 30) {
        upNext.splice(i, 1);
        offset.push(o);
      }
    }

    return { overdue, upNext, offset, dineIn };
  }, [orders]);

  const renderItem = (item: any, key: React.Key): JSX.Element => (
    <div key={key} className={styles.itemLine}>
      <div className={styles.itemMain}>
        <span className={styles.qty}>{item.quantity}</span>
        <span className={styles.itemName}>{item.name}</span>
        {item.size ? <span className={styles.itemSize}>({item.size})</span> : null}
      </div>
      {item.comment ? <div className={styles.note}>Note: {item.comment}</div> : null}
      {item.modifiers && item.modifiers.length ? (
        <div className={styles.modifiers}>
          {item.modifiers.map((m: any, idx: number) => (
            <div key={idx} className={styles.modRow}>
              <span className={styles.qty}>{m.quantity}</span>
              <span className={styles.modName}>{m.name}</span>
              {m.comment ? <span className={styles.modNote}> - {m.comment}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const renderOrder = (o: HydratedOrder): JSX.Element => (
    <div key={o.orderId} className={styles.orderCard}>
      <div className={styles.orderHeader}>
        <div className={styles.orderTitle}>Order #{o.orderId}</div>
        <div className={styles.orderMeta}>
          <span>Pickup: {new Date(o.pickupTime).toLocaleString()}</span>
          {o.tableInfo?.tableName ? <span>Table: {o.tableInfo.tableName}</span> : null}
          {o.identityInfo?.mappedCustomerResponses?.map((response, idx) => (
            <span key={idx}>{response.label}: {response.value}</span>
          ))}
          {o.status ? <span>Status: {String(o.status)}</span> : null}
        </div>
      </div>
      <br />
      <div className={styles.itemsSection}>{o.items?.map((it: any, idx: number) => renderItem(it, idx))}</div>
    </div>
  );

  const pageTitle = fromDate && toDate && isValid(fromDate) && isValid(toDate)
    ? `Upcoming Orders - ${formatISO(fromDate, { representation: 'date' })} to ${formatISO(toDate, { representation: 'date' })}`
    : `Upcoming Orders - ${normalizeDay}`;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>{pageTitle}</h1>
        <div className={styles.headerMeta}>
          <span>Location: {locationTitle || locationId}</span>
          {query ? <span>Filter: {decodeURIComponent(query)}</span> : null}
          <span>Printed: {new Date().toLocaleString()}</span>
        </div>
        {loading ? <div className={styles.loading}>Loading orders…</div> : null}
      </div>

      {!loading && (
        <>
          {groups.dineIn.length ? (
            <section className={styles.section}>
              <h2>Dine-In</h2>
              {groups.dineIn.map(renderOrder)}
            </section>
          ) : null}

          {groups.overdue.length ? (
            <section className={styles.section}>
              <h2>Overdue</h2>
              {groups.overdue.map(renderOrder)}
            </section>
          ) : null}

          {groups.upNext.length ? (
            <section className={styles.section}>
              <h2>Up Next</h2>
              {groups.upNext.map(renderOrder)}
            </section>
          ) : null}

          {groups.offset.length ? (
            <section className={styles.section}>
              <h2>30+ minutes</h2>
              {groups.offset.map(renderOrder)}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
};

export default UpcomingOrdersPrintPage;


